import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { sqModalSearchStore } from '@/core/core.stores';
import { onEventPreventPropagation } from '@/core/onEnterKeypress.util';
import { SearchWidget } from '@/search/SearchWidget.organism';
import { SearchStoreProvider } from '@/search/search.store';
import { isAsset } from '@/utilities/utilities';
import { Checkbox } from '@seeqdev/qomponents';
import type { SearchWidgetSharedProps } from '@/search/SearchWidget.organism';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SelectItemSearchWidgetProps extends SearchWidgetSharedProps {
  onSelect: (item: SelectedSearchItem) => void;
  iconPartialTooltipKey: string;
  selectedItems?: SelectedSearchItem[];
  validateItem: (item: SelectedSearchItem) => Promise<boolean>;
  modalId: string;
  isMultiple?: boolean;
  showRecentlyAccessed?: boolean;
  /**
   * When only `Asset` is being searched, non-asset results are normally not selectable. Set this to keep them
   * selectable without adding a second (extracted) type to `searchTypes` — mixing an extracted type such as
   * AssetSelection with a non-extracted type in one search is rejected by the backend (CRAB-61975).
   */
  allowSelectingNonAssets?: boolean;
  restrictExploration?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
  initialScalingTable?: SelectedSearchItem;
  /** Optional filter function to further filter items in the search results */
  additionalItemFilter?: (items: SelectedSearchItem[]) => SelectedSearchItem[];
}

export const SelectItemSearchWidget = ({
  onSelect,
  iconPartialTooltipKey,
  selectedItems,
  validateItem,
  scopeIds,
  excludeGloballyScoped = false,
  modalId,
  searchTypes,
  isMultiple = false,
  allowSelectingNonAssets = false,
  selectAllCallback,
  areAllSelected = () => false,
  showPinnedAssets = false,
  showOnlyScopedAssets = false,
  showOnlyConditions = false,
  showRecentlyAccessed = false,
  overrideWorkbookId,
  filterByWorkbook,
  itemTypeToShowInOverview,
  overviewLabel,
  showCreateNewItem = false,
  onCreateNewItem,
  createNewItemMessage = '',
  createNewItemLabel = '',
  doNotDisableOnEdit = false,
  restrictExploration = false,
  initialItemTable,
  initialHierarchy,
  additionalItemFilter,
  initialScalingTable,
}: SelectItemSearchWidgetProps) => {
  const { t } = useTranslation();

  const isSelectingAssets = searchTypes.includes(SeeqNames.Types.Asset);

  const searchResultIcons = useCallback(
    async (item: SelectedSearchItem): Promise<React.ReactNode> => {
      const onlySearchingAssets = isSelectingAssets && searchTypes.length === 1 && !allowSelectingNonAssets;
      if (!isAsset(item) && onlySearchingAssets) {
        return Promise.resolve(null);
      }

      const isValid = await validateItem(item);
      const isSelected = selectedItems?.some((selectedItem) => selectedItem.id === item.id);

      return isValid ? (
        <Checkbox
          type={isMultiple ? 'checkbox' : 'radio'}
          testId="itemSelect"
          extraClassNames="ml5"
          tooltip={t(`${iconPartialTooltipKey}.ASSET_TOOLTIP`)}
          tooltipPlacement="top"
          checked={isSelected}
          onClick={onEventPreventPropagation(() => onSelect(item as SelectedSearchItem))}
        />
      ) : (
        <Checkbox
          type={isMultiple ? 'checkbox' : 'radio'}
          testId="itemSelectInvalid"
          extraClassNames="disabled ml5"
          tooltip={t(`${iconPartialTooltipKey}.ASSET_TOOLTIP_INVALID`)}
          onClick={onEventPreventPropagation(_.noop)}
          disabled={true}
        />
      );
    },
    [selectedItems, validateItem, iconPartialTooltipKey, onSelect],
  );

  return (
    <SearchStoreProvider store={sqModalSearchStore}>
      <SearchWidget
        isSelectingAsset={isSelectingAssets}
        iconClasses="sq-fairly-dark-gray"
        searchTypes={searchTypes}
        searchResultIcons={searchResultIcons}
        allowAssetReplacement={false}
        showOnlyResults={
          !showRecentlyAccessed && !(searchTypes.includes(SeeqNames.Types.Asset) && searchTypes.length > 2)
        }
        showPinnedAssets={showPinnedAssets}
        scopeIds={scopeIds}
        excludeGloballyScoped={excludeGloballyScoped}
        modalId={modalId}
        predefinedSearchTypes={searchTypes}
        onSelectItem={onSelect}
        selectAllCallback={selectAllCallback}
        areAllSelected={areAllSelected}
        showOnlyScopedAssets={showOnlyScopedAssets}
        showOnlyConditions={showOnlyConditions}
        overrideWorkbookId={overrideWorkbookId}
        filterByWorkbook={filterByWorkbook}
        itemTypeToShowInOverview={itemTypeToShowInOverview}
        overviewLabel={overviewLabel}
        showCreateNewItem={showCreateNewItem}
        onCreateNewItem={onCreateNewItem}
        createNewItemMessage={createNewItemMessage}
        createNewItemLabel={createNewItemLabel}
        doNotDisableOnEdit={doNotDisableOnEdit}
        restrictExploration={restrictExploration}
        initialItemTable={initialItemTable}
        initialHierarchy={initialHierarchy}
        initialScalingTable={initialScalingTable}
        additionalItemFilter={additionalItemFilter}
      />
    </SearchStoreProvider>
  );
};
